import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { AttachmentSelectorComponent } from "@components/attachment-selector/attachment-selector.component";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ProductConfigStepperBarComponent } from "@components/stepper/product-config-stepper-bar/product-config-stepper-bar.component";
import { Gas } from "@domain/gas/gas";
import { FlameDetectionRange } from "@domain/product/flame-detection-range";
import { FgdsProductType, LocalizeProductNamePipe, Product } from "@domain/product/product";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { WizardComponent } from "@odx/angular/components/wizard";
import { FlameDetectorType } from "@project/flame-detector-configuration/flame-detector-type";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { OriginalProductConfigurationFactory } from "@project/original-product-configuration-factory";
import { ProductConfigurationService } from "@project/product-configuration.service";
import { ProductItem } from "@project/product-item";
import { GasSelectorComponent } from "@project/transmitter-configuration/gas-selector/gas-selector.component";
import { forkJoin } from "rxjs";
import { FlameDetectorFamilySelectorComponent } from "./flame-detector-family-selector/flame-detector-family-selector.component";
import { FlameDetectorVariantSelectorComponent } from "./flame-detector-variant-selector/flame-detector-variant-selector.component";
import { OriginalFlameDetectorConfiguration } from "./original-flame-detector-configuration";

@Component({
  selector: "app-flame-detector-configuration",
  templateUrl: "./flame-detector-configuration.component.html",
  styleUrls: ["./flame-detector-configuration.component.scss"],
  standalone: true,
  imports: [
    UiKitModule,
    ConfigurationWizardBackButtonComponent,
    GasSelectorComponent,
    FlameDetectorFamilySelectorComponent,
    FlameDetectorVariantSelectorComponent,
    AttachmentSelectorComponent,
    ProductConfigStepperBarComponent,
  ],
  providers: [OriginalProductConfigurationFactory, LocalizeProductNamePipe],
})
export class FlameDetectorConfigurationComponent implements OnInit {
  private activeRoute = inject(ActivatedRoute);
  private originalProductConfigurationFactory = inject(OriginalProductConfigurationFactory);
  private productConfigurationService = inject(ProductConfigurationService);
  private localizeProductNamePipe = inject(LocalizeProductNamePipe);

  protected project!: Project;
  protected selectedSubstance?: Gas;
  protected selectedFamily?: FlameDetectorType;
  protected selectedVariant?: Product;
  protected selectedAttachments: { product: Product; quantity: number }[] = [];
  protected activeStep = 0;
  protected steps = 4;
  protected originalConfiguration?: OriginalFlameDetectorConfiguration;
  protected isIncompleteConfiguration = false;

  protected readonly FloorplanMenuType = FloorplanMenuType;
  protected readonly FgdsProductType = FgdsProductType;

  @ViewChild("configurationWizard")
  private configurationWizard!: WizardComponent;

  @ViewChild("backButton")
  private backButton!: ConfigurationWizardBackButtonComponent;

  ngOnInit(): void {
    this.project = this.activeRoute.parent!.snapshot.data["project"];

    if (this.activeRoute.snapshot.queryParams["config_id"]) {
      const configId = this.activeRoute.snapshot.queryParams["config_id"];
      const originalConfiguration$ = this.originalProductConfigurationFactory.createOriginalFlameDetectorConfiguration(
        this.project,
        configId,
      );
      const incompleteConfigurationIds$ = this.productConfigurationService.getIncompleteConfigIds(this.project);
      forkJoin([originalConfiguration$, incompleteConfigurationIds$]).subscribe(
        ([originalConfiguration, incompleteConfigurationIds]) => {
          this.originalConfiguration = originalConfiguration as OriginalFlameDetectorConfiguration;
          this.isIncompleteConfiguration = incompleteConfigurationIds.includes(configId);
        },
      );
    }
  }

  protected onSubmit() {
    if (this.activeStep === 0) {
      if (!this.selectedSubstance) return;
    } else if (this.activeStep === 1) {
      if (!this.selectedFamily) return;
    } else if (this.activeStep === 2) {
      if (!this.selectedVariant) return;
    } else if (this.activeStep === 3) {
      if (this.originalConfiguration) {
        this.updateFlameDetectorConfig(this.originalConfiguration.productConfiguration);
      } else {
        this.createNewFlameDetectorConfig();
      }
    }
    this.configurationWizard.setActiveStepValid();
    this.configurationWizard.nextStep();
  }

  protected setActiveStep(event: number) {
    if (event === 0 && this.originalConfiguration && !this.isIncompleteConfiguration) {
      this.configurationWizard.setActiveStepValid();
      this.configurationWizard.nextStep();
    } else {
      this.activeStep = event;
    }
  }

  protected selectSubstance(substance: Gas) {
    if (!substance) return;
    if (this.originalConfiguration && !this.isIncompleteConfiguration) {
      this.configurationWizard.setActiveStepValid();
      this.configurationWizard.nextStep();
    }
    this.selectedSubstance = substance;
    this.selectedFamily = undefined;
    this.selectedVariant = undefined;
  }

  protected selectFamily(family: FlameDetectorType) {
    this.selectedFamily = family;
    this.selectedVariant = undefined;
  }

  protected selectVariant(variant: Product) {
    this.selectedVariant = variant;
  }

  protected onPrevious() {
    this.configurationWizard.previousStep();
  }

  private createNewFlameDetectorConfig() {
    const detectionRange = this.resolveDetectionRange();
    const config = FlameDetectorConfiguration.create(
      this.localizeProductNamePipe.transform(this.selectedVariant!),
      this.project,
      this.selectedVariant!.id,
      this.selectedAttachments.map((a) => ProductItem.create(a.product.id, a.quantity)),
      detectionRange,
      this.selectedSubstance?.id,
      this.selectedFamily?.key,
    );
    this.project.addFlameDetectors(config);
    this.backButton.navigateBack();
  }

  private updateFlameDetectorConfig(originalConfig: FlameDetectorConfiguration) {
    const attachmentIds = this.selectedAttachments.map((a) => ProductItem.create(a.product.id, a.quantity));
    const detectionRange = this.resolveDetectionRange();
    originalConfig.update(this.selectedSubstance!.id, this.selectedVariant!, attachmentIds, detectionRange, this.selectedFamily);
    this.backButton.navigateBack();
  }

  private resolveDetectionRange(): FlameDetectionRange {
    const substanceName = this.selectedSubstance!.definition.localizedName["en-US"];
    const casNumber = this.selectedSubstance!.definition.casNumber;
    const raw =
      this.selectedVariant?.flameDetectionData?.find((entry) => entry.name === substanceName) ??
      this.selectedVariant?.flameDetectionData?.find((entry) => entry.name === casNumber);
    const defaultRange = raw ? raw.detectionRangeDefault : 0;
    return FlameDetectionRange.create(substanceName, defaultRange, raw?.detectionRangeHigh, raw?.detectionRangeLow);
  }
}
